import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faPlusSquare } from "@fortawesome/free-solid-svg-icons";
import HelpGuideModal from "./HelpGuideModal";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/InstallAppPanel.css";

// ─── Platform & browser detection helpers ────────────────────────────────────
const getBrowserPlatformInfo = () => {
  const ua = navigator.userAgent || "";
  const navPlatform = navigator.platform || "";

  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navPlatform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS
  const isAndroid = /Android/.test(ua);
  const isMac = !isIOS && /Mac/.test(navPlatform);

  // Third-party iOS browsers use their own UA tokens
  const isChromeiOS = /CriOS/.test(ua);
  const isFirefoxiOS = /FxiOS/.test(ua);
  const isEdgeiOS = /EdgiOS/.test(ua);

  // Android / Desktop browsers
  const isSamsungBrowser = /SamsungBrowser/.test(ua);
  const isFirefox = /Firefox\//.test(ua) && !isFirefoxiOS;
  const isEdge = /Edg\//.test(ua) && !isEdgeiOS;
  const isChrome =
    /Chrome\//.test(ua) &&
    !isEdge &&
    !isSamsungBrowser &&
    !isChromeiOS &&
    !/Chromium/.test(ua);
  const isSafariDesktop =
    isMac && /Safari\//.test(ua) && !isChrome && !isEdge && !isFirefox;

  const platform = isIOS
    ? "ios"
    : isAndroid
      ? "android"
      : isMac
        ? "mac"
        : "windows";

  let browser;
  if (isChromeiOS) browser = "chrome-ios";
  else if (isFirefoxiOS) browser = "firefox-ios";
  else if (isEdgeiOS) browser = "edge-ios";
  else if (isIOS)
    browser = "safari-ios"; // default iOS browser
  else if (isSamsungBrowser) browser = "samsung";
  else if (isFirefox) browser = "firefox";
  else if (isEdge) browser = "edge";
  else if (isSafariDesktop) browser = "safari-desktop";
  else if (isChrome) browser = "chrome";
  else browser = "chrome"; // safe default

  return { platform, browser };
};

const isRunningStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/**
 * Builds a HelpGuideModal guide object for "Add to Home Screen" instructions,
 * ordered so the user's detected browser is the first (active) tab.
 */
const buildInstallGuide = (platform, browser) => {
  // ── iOS browser step sets ──────────────────────────────────────────────────
  const iosSafari = {
    name: "Safari",
    steps: [
      {
        instruction:
          "Tap the Share button (box with arrow) in the Safari toolbar.",
        detail:
          "If you don't see it, look for the (...) button at the bottom right of the screen, then choose Share. The (...) button may be at the bottom or top depending on your Safari layout.",
      },
      {
        instruction: "Scroll down the share sheet and tap Add to Home Screen.",
        highlight: "Add to Home Screen",
        detail:
          "On iOS 17 and later you can also tap 'Open as Web App' for a more app-like, full-screen experience.",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep:
      "KinShield now appears on your home screen and opens like a native app.",
  };

  const iosChrome = {
    name: "Chrome",
    steps: [
      {
        instruction:
          "Tap the Share button (box with up arrow) in the top URL bar.",
        highlight: "Share",
        detail:
          "Requires iOS 16.4 or later. If you don't see the Share button in the URL bar, try scrolling up or tapping the address bar first.",
      },
      {
        instruction: "Tap on View More, then Add to Home Screen.",
        highlight: "Add to Home Screen",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep:
      "KinShield now appears on your home screen and opens like a native app.",
  };

  const iosFirefox = {
    name: "Firefox",
    steps: [
      {
        instruction:
          "Firefox on iOS doesn't support home screen installation directly — you need to open the page in Safari.",
        detail:
          "Tap the URL in Firefox's address bar, copy it, open Safari, and paste it there. Or tap the Share button in Firefox and choose Safari.",
      },
      {
        instruction:
          "In Safari, tap the Share button (box with arrow) in the toolbar.",
        highlight: "Share",
      },
      {
        instruction: "Scroll down and tap Add to Home Screen.",
        highlight: "Add to Home Screen",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep:
      "KinShield now appears on your home screen and opens like a native app.",
  };

  const iosEdge = {
    name: "Edge",
    steps: [
      {
        instruction: "Tap the menu icon (···) at the bottom of the screen.",
        highlight: "···",
      },
      {
        instruction: "Tap Add to Phone or Add to Home Screen.",
        highlight: "Add to Phone",
        detail: "The option label may vary by Edge version.",
      },
      {
        instruction: "Tap Add in the confirmation dialog.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield now appears on your home screen.",
  };

  // ── Android browser step sets ──────────────────────────────────────────────
  const androidChrome = {
    name: "Chrome",
    steps: [
      {
        instruction: "Tap the menu icon (⋮) in the top-right corner of Chrome.",
        highlight: "⋮",
      },
      {
        instruction: "Tap Add to Home Screen or Install App.",
        highlight: "Add to Home Screen",
        detail:
          "Newer versions of Chrome may show 'Install App' for sites that support it, or 'Add to Home Screen' for others.",
      },
      {
        instruction: "Review the name and tap Add or Install to confirm.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield now appears on your Android home screen.",
  };

  const androidSamsung = {
    name: "Samsung Internet",
    steps: [
      {
        instruction:
          "Tap the menu icon (☰) at the bottom-right of the screen.",
        highlight: "☰",
      },
      {
        instruction: "Tap Add page to, then select Home Screen.",
        highlight: "Home Screen",
      },
      {
        instruction: "Tap Add to confirm.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield now appears on your home screen.",
  };

  const androidFirefox = {
    name: "Firefox",
    steps: [
      {
        instruction: "Tap the menu icon (⋮) in the top-right corner.",
        highlight: "⋮",
      },
      {
        instruction:
          "Tap Install from the menu. If you don't see Install, tap Add to Home Screen instead.",
        highlight: "Install",
      },
      {
        instruction: "Tap Add in the confirmation dialog.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield now appears on your home screen.",
  };

  const androidEdge = {
    name: "Edge",
    steps: [
      {
        instruction: "Tap the menu icon (···) at the bottom of the screen.",
        highlight: "···",
      },
      {
        instruction: "Tap Add to Phone or Add to Home Screen.",
        highlight: "Add to Phone",
        detail: "The option label may vary by Edge version.",
      },
      {
        instruction: "Tap Add to confirm.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield now appears on your home screen.",
  };

  // ── Desktop browser step sets ──────────────────────────────────────────────
  const desktopChrome = {
    name: "Chrome",
    steps: [
      {
        instruction:
          "Look for the <img src='/ChromeInstallIcon.png' alt='install icon' style='height:1.1em;vertical-align:middle;display:inline;margin:0 2px;' /> icon in Chrome's address bar and click it.",
        detail:
          "Don't see it? Open Chrome's menu (⋮), go to Cast, save, and share, then click Install page as app.",
      },
      {
        instruction: "Click Install in the dialog that appears.",
        highlight: "Install",
      },
    ],
    finalStep:
      "KinShield opens as a standalone app window, separate from your browser.",
  };

  const desktopEdge = {
    name: "Edge",
    steps: [
      {
        instruction:
          "Look for the install icon in Edge's address bar and click it.",
        highlight: "install icon",
        detail:
          "Don't see it? Open Edge's menu (···), choose Apps, then Install this site as an app.",
      },
      {
        instruction: "Click Install in the dialog that appears.",
        highlight: "Install",
      },
    ],
    finalStep: "KinShield opens as a standalone app window.",
  };

  const desktopFirefox = {
    name: "Firefox",
    steps: [
      {
        instruction:
          "Firefox doesn't support installing web apps to the desktop natively.",
        detail:
          "For the best experience, open KinShield in Chrome or Edge and install it from there.",
      },
      {
        instruction:
          "To bookmark KinShield for quick access, press Ctrl + D (Windows) or ⌘ + D (Mac).",
      },
    ],
    finalStep:
      "Bookmark saved! You can access KinShield quickly from your bookmarks bar.",
  };

  const desktopSafari = {
    name: "Safari",
    steps: [
      {
        instruction:
          "Open Safari's File menu and click Add to Dock, or click the Share button in the toolbar and choose Add to Dock.",
        highlight: "Add to Dock",
        detail:
          "Add to Dock requires macOS Sonoma 14 or later. On older macOS, use Chrome or Edge instead.",
      },
      {
        instruction:
          "Edit the name if you like, then click Add. KinShield is saved to your Applications folder and appears in your Dock.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield is now in your Dock and opens as a standalone app.",
  };

  // ── Assemble guide per platform, with detected browser first ──────────────
  const reorder = (list, primaryIdx) => [
    list[primaryIdx],
    ...list.filter((_, i) => i !== primaryIdx),
  ];

  if (platform === "ios") {
    const list = [iosSafari, iosChrome, iosFirefox, iosEdge];
    const order = {
      "safari-ios": 0,
      "chrome-ios": 1,
      "firefox-ios": 2,
      "edge-ios": 3,
    };
    return {
      title: "Add KinShield to Your Home Screen",
      subtitle: "Steps for your browser are shown first.",
      platforms: reorder(list, order[browser] ?? 0),
      finalStep: "KinShield now appears on your home screen.",
    };
  }

  if (platform === "android") {
    const list = [androidChrome, androidSamsung, androidFirefox, androidEdge];
    const order = { chrome: 0, samsung: 1, firefox: 2, edge: 3 };
    return {
      title: "Add KinShield to Your Home Screen",
      subtitle: "Steps for your browser are shown first.",
      platforms: reorder(list, order[browser] ?? 0),
      finalStep: "KinShield now appears on your home screen.",
    };
  }

  if (platform === "mac") {
    const list = [desktopChrome, desktopEdge, desktopSafari, desktopFirefox];
    const order = { chrome: 0, edge: 1, "safari-desktop": 2, firefox: 3 };
    return {
      title: "Install KinShield on Your Mac",
      subtitle: "Steps for your browser are shown first.",
      platforms: reorder(list, order[browser] ?? 0),
      finalStep: "KinShield is now easily accessible on your Mac.",
    };
  }

  // Windows (default)
  const list = [desktopChrome, desktopEdge, desktopFirefox];
  const order = { chrome: 0, edge: 1, firefox: 2 };
  return {
    title: "Install KinShield on Windows",
    subtitle: "Steps for your browser are shown first.",
    platforms: reorder(list, order[browser] ?? 0),
    finalStep: "KinShield is now easily accessible on your desktop.",
  };
};

/**
 * Builds a HelpGuideModal guide for users whose shortcut was deleted but whose
 * browser still considers the PWA installed (suppressing beforeinstallprompt).
 * Walks them through restoring the shortcut via the browser's app manager.
 */
const buildReinstallGuide = (browser, platform) => {
  const chromeDesktop = {
    name: "Chrome",
    steps: [
      {
        instruction:
          "Check the right end of Chrome's address bar for a <img src='/ChromeInstallIcon.png' alt='install icon' style='height:1.1em;vertical-align:middle;display:inline;margin:0 2px;' /> icon. If you see it, click it and choose Install.",
        detail:
          "Chrome sometimes re-enables the icon shortly after a shortcut is deleted. If it's there, you're done after this step.",
      },
      {
        instruction:
          "If no icon is visible, open a new tab and type this address, then press Enter:",
        highlight: "chrome://apps",
        copyable: true,
      },
      {
        instruction:
          "Find the KinShield tile in the list. Right-click it and choose Create shortcut\u2026",
        highlight: "Create shortcut\u2026",
        detail:
          "If you don't see KinShield here, Chrome has fully uninstalled it — close this guide and click the Install button again to reinstall from scratch.",
      },
      {
        instruction:
          "Check Desktop (and optionally Taskbar), then click Create.",
        highlight: "Create",
      },
    ],
    finalStep:
      "KinShield shortcut is restored on your Desktop. You can launch it anytime without opening Chrome first.",
  };

  const edgeDesktop = {
    name: "Edge",
    steps: [
      {
        instruction:
          "Check the right end of Edge's address bar for a \u2295 install icon. If you see it, click it and choose Install.",
        highlight: "\u2295",
        detail:
          "Edge may re-enable this icon after a short delay when it detects the shortcut is gone.",
      },
      {
        instruction:
          "If no icon is visible, open a new tab and type this address, then press Enter:",
        highlight: "edge://apps",
        copyable: true,
      },
      {
        instruction:
          "Find KinShield in the list. Click its three-dot menu (\u00b7\u00b7\u00b7) and choose Pin to taskbar or Create desktop shortcut.",
        highlight: "Create desktop shortcut",
      },
    ],
    finalStep:
      "KinShield shortcut is restored. You can launch it from your Desktop or Taskbar.",
  };

  const chromeAndroid = {
    name: "Chrome",
    steps: [
      {
        instruction: "Tap the menu icon (\u22ee) at the top-right of Chrome.",
        highlight: "\u22ee",
      },
      {
        instruction: "Tap Add to Home Screen or Install App.",
        highlight: "Add to Home Screen",
        detail:
          "Newer Chrome versions may show 'Install App' for sites that support it.",
      },
      { instruction: "Tap Add or Install to confirm.", highlight: "Add" },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  const samsungAndroid = {
    name: "Samsung Internet",
    steps: [
      {
        instruction:
          "Tap the menu icon (\u2630) at the bottom-right of the screen.",
        highlight: "\u2630",
      },
      {
        instruction: "Tap Add page to, then select Home Screen.",
        highlight: "Home Screen",
      },
      { instruction: "Tap Add to confirm.", highlight: "Add" },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  // ── iOS browser step sets (reinstall) ──────────────────────────────────────
  const iosSafariReinstall = {
    name: "Safari",
    steps: [
      {
        instruction:
          "Tap the Share button (box with arrow) in the Safari toolbar.",
        detail:
          "If you don't see it, tap the address bar to reveal the toolbar, then tap the More button (···) and choose Share.",
      },
      {
        instruction:
          "Scroll down in the share sheet and tap Add to Home Screen.",
        highlight: "Add to Home Screen",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  const iosChromeReinstall = {
    name: "Chrome",
    steps: [
      {
        instruction:
          "Tap the Share button (box with arrow) in the URL bar or at the bottom of the screen.",
        highlight: "Share",
        detail:
          "Requires iOS 16.4 or later. If you don't see the Share button in the URL bar, try scrolling up or tapping the address bar first.",
      },
      {
        instruction: "Tap Add to Home Screen.",
        highlight: "Add to Home Screen",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  const iosFirefoxReinstall = {
    name: "Firefox",
    steps: [
      {
        instruction:
          "Firefox on iOS doesn't support home screen installation directly — open the page in Safari first.",
        detail:
          "Copy the URL from Firefox's address bar, open Safari, and paste it there. Or tap the Share button in Firefox and choose Safari.",
      },
      {
        instruction:
          "In Safari, tap the Share button (box with arrow) in the toolbar.",
        highlight: "Share",
      },
      {
        instruction: "Scroll down and tap Add to Home Screen.",
        highlight: "Add to Home Screen",
      },
      {
        instruction: "Edit the name if you like, then tap Add.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  const iosEdgeReinstall = {
    name: "Edge",
    steps: [
      {
        instruction: "Tap the menu icon (···) at the bottom of the screen.",
        highlight: "···",
      },
      {
        instruction: "Tap Add to Phone or Add to Home Screen.",
        highlight: "Add to Phone",
        detail: "The option label may vary by Edge version.",
      },
      {
        instruction: "Tap Add in the confirmation dialog.",
        highlight: "Add",
      },
    ],
    finalStep: "KinShield icon is back on your home screen.",
  };

  if (platform === "ios") {
    const iosOrder = {
      "safari-ios": 0,
      "chrome-ios": 1,
      "firefox-ios": 2,
      "edge-ios": 3,
    };
    const iosList = [
      iosSafariReinstall,
      iosChromeReinstall,
      iosFirefoxReinstall,
      iosEdgeReinstall,
    ];
    const iosPrimaryIdx = iosOrder[browser] ?? 0;
    return {
      title: "Restore KinShield to Your Home Screen",
      subtitle:
        "It looks like the icon was removed. Here's how to add it back.",
      platforms: [
        iosList[iosPrimaryIdx],
        ...iosList.filter((_, i) => i !== iosPrimaryIdx),
      ],
      finalStep: "KinShield icon is back on your home screen.",
    };
  }

  if (platform === "android") {
    const list =
      browser === "samsung"
        ? [samsungAndroid, chromeAndroid]
        : [chromeAndroid, samsungAndroid];
    return {
      title: "Restore KinShield to Your Home Screen",
      subtitle:
        "It looks like the icon was removed. Here's how to add it back.",
      platforms: list,
      finalStep: "KinShield icon is back on your home screen.",
    };
  }

  // Desktop (Windows or Mac)
  const list =
    browser === "edge"
      ? [edgeDesktop, chromeDesktop]
      : [chromeDesktop, edgeDesktop];
  return {
    title: "Restore KinShield Shortcut",
    subtitle:
      "Your browser remembers the previous installation. Follow these steps to restore the shortcut.",
    platforms: list,
    finalStep: "KinShield shortcut is restored on your Desktop.",
  };
};

/**
 * InstallAppPanel
 *
 * Self-contained panel that handles PWA install / add-to-home-screen for
 * KinShield. Detects the current browser and platform, shows the appropriate
 * Install / Add to Homepage buttons (or an "already installed" confirmation),
 * and launches the step-by-step HelpGuideModal when needed.
 *
 * Designed to be dropped into any page — carries all required state and
 * side-effects internally.
 */
function InstallAppPanel() {
  const { t } = useTranslation();
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showReinstallGuide, setShowReinstallGuide] = useState(false);
  const [{ platform: installPlatform, browser: installBrowser }] = useState(
    () => getBrowserPlatformInfo(),
  );
  const [installDone, setInstallDone] = useState(false);
  // True when the browser's PWA registry still lists KinShield as installed
  // (e.g. the user deleted the shortcut without going through chrome://apps)
  const [browserConsidersInstalled, setBrowserConsidersInstalled] =
    useState(false);

  useEffect(() => {
    if (typeof navigator.getInstalledRelatedApps === "function") {
      navigator
        .getInstalledRelatedApps()
        .then((apps) => setBrowserConsidersInstalled(apps.length > 0))
        .catch(() => {});
    }
  }, []);

  // True for browsers that support the native beforeinstallprompt API
  const canNativeInstall =
    installPlatform !== "ios" &&
    ["chrome", "edge", "samsung"].includes(installBrowser);

  // Running in standalone (launched from installed shortcut)
  const alreadyInstalled = isRunningStandalone();

  // Unified "app is already on this device" signal:
  //  • explicitly running as standalone PWA
  //  • native install just completed
  const appIsInstalled = alreadyInstalled || installDone;

  // Capture the beforeinstallprompt event (Android / Desktop Chrome & Edge).
  // Also cancel the "already installed" timeout — the prompt proves it isn't.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Triggers the browser's native PWA install prompt (Chrome / Edge on desktop & Android).
  // Falls back to the general install guide if the prompt hasn't fired yet.
  const handleInstall = async () => {
    if (installPromptEvt) {
      await installPromptEvt.prompt();
      const { outcome } = await installPromptEvt.userChoice;
      if (outcome === "accepted") {
        setInstallDone(true);
        setInstallPromptEvt(null);
      }
    } else {
      // Prompt not yet captured (PWA criteria not fully met in this environment)
      setShowInstallGuide(true);
    }
  };

  // Opens the step-by-step guide for browsers / platforms that need manual steps
  const handleAddToHomepage = () => {
    setShowInstallGuide(true);
  };

  return (
    <>
      {/* ── Install / Add to Homepage buttons ── */}
      <div className="install-app-row">
        <p className="install-app-description">
          {t("installApp.description")}
          <br />
        </p>
        {appIsInstalled && !browserConsidersInstalled ? (
          /* Already installed and shortcut is intact */
          <p className="install-app-installed">
            <FontAwesomeIcon icon={faCheckCircle} />
            {t("installApp.alreadyInstalled")}
          </p>
        ) : browserConsidersInstalled ? (
          /* Browser registry has the PWA but the shortcut was deleted —
             don't offer Install (it won't work); offer Restore instead */
          <div className="install-app-btn-group">
            <p className="install-app-installed">
              <FontAwesomeIcon icon={faCheckCircle} />
              {t("installApp.shortcutRemoved")}
            </p>
            <button
              className="install-app-btn install-app-btn-install"
              onClick={() => setShowReinstallGuide(true)}
            >
              <FontAwesomeIcon icon={faPlusSquare} />
              {t("installApp.restoreShortcut")}
            </button>
          </div>
        ) : (
          <div className="install-app-btn-group">
            {canNativeInstall ? (
              /* Native install — triggers browser prompt or falls back to guide */
              <button
                className="install-app-btn install-app-btn-install"
                onClick={handleInstall}
                title={
                  installPromptEvt
                    ? t("installApp.installTitle")
                    : t("installApp.installTitleFallback")
                }
              >
                <FontAwesomeIcon icon={faPlusSquare} />
                {t("installApp.install")}
              </button>
            ) : (
              /* Manual instructions guide for iOS / Firefox and other unsupported browsers */
              <button
                className="install-app-btn install-app-btn-add-home"
                onClick={handleAddToHomepage}
              >
                <FontAwesomeIcon icon={faPlusSquare} />
                {t("installApp.addToHomepage")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Add to Home Screen guide modal ── */}
      {showInstallGuide && (
        <HelpGuideModal
          guide={buildInstallGuide(installPlatform, installBrowser)}
          onClose={() => setShowInstallGuide(false)}
        />
      )}

      {/* ── Reinstall / restore-shortcut guide modal ── */}
      {showReinstallGuide && (
        <HelpGuideModal
          guide={buildReinstallGuide(installBrowser, installPlatform)}
          onClose={() => setShowReinstallGuide(false)}
        />
      )}
    </>
  );
}

export default InstallAppPanel;
